import styled, { useTheme } from 'styled-components';
import { PHONE_MEDIA_QUERY } from '../../../../../common/responsive';
import { useEffect, useRef, useState } from 'react';
import Button from '../../../../../components/Button';
import { useGiftForm } from '../../../../../components/GiftFormContext';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
  selectTokensWithUSDBalance,
} from '../../../../../redux/slices/tokenSlice';
import { useDispatch, useSelector } from 'react-redux';
import WalletSwapIcon from '../../../../../assets/icons/wallet-swap.svg?react';
import { fromTokenAmount, toTokenAmount } from '../../../../../hooks/useSolana';
import ChevronRightIcon from '../../../../../assets/icons/chevron-right.svg?react';
import { openModal } from '../../../../../redux/slices/modals';
import { useCreateGiftMutation } from '../../../../../redux/api/authorized/gifts';
import {
  EVENT_GIFTCARD_CREATED,
  sendEvent,
} from '../../../../../common/analytics';
import { BodyLarge } from '../../../../../components/Texts/index';
import { hapticsImpactLight } from '../../../../../common/haptic';
import { selectTokenBalance } from '../../../../../redux/slices/walletSlice';
import {
  formatTokenPrice,
  formatUSDEnsure2Digits,
} from '../../../../../common/currency';
import { useLazyCreateOrderQuery } from '../../../../../redux/api/authorized/ultra';

const StyledInput = styled.input`
  height: 48px;
  background-color: red;
  display: inline-block;
  width: fit-content;
  flex: 0 0 auto;
  border-radius: 16px;
  text-align: center;
  border: none;
  margin-left: -14px;
  margin-right: -14px;
  text-align: center;
  font-size: clamp(20px, 9vw, 64px);
  font-weight: 700;
  background-color: transparent;

  color: ${({ theme }) => theme.text.title};
  outline: none;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(14px, 9vw, 64px);
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: fit-content;
  position: relative;
  flex: 0 0 auto;
  max-width: 100%;
  padding-left: 14px;
  padding-right: 14px;
  overflow: hidden;
`;

const InsideInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  align-items: center;
  pointer-events: none;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  flex-wrap: nowrap;
  text-align: center;
  font-size: clamp(20px, 9vw, 64px);
  font-weight: 700;
  color: ${({ theme }) => theme.text.title};
  text-wrap: nowrap;
  &:focus {
    outline: none;
  }
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(14px, 9vw, 64px);
  }
`;

const AmountButton = styled.button`
  flex: 1;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  color: ${({ theme }) => theme.text.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AmountButtonContainer = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
`;

const SwapAmountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  cursor: pointer;
`;

const AmountsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const PaymentSelectorContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 12px;
  cursor: pointer;
  justify-content: flex-start;
  align-items: center;
  width: fit-content;
`;

const TokenImage = styled.img`
  width: 24px;
  height: 24px;
`;

const PaymentTokenInfoText = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
`;

const PaymentSelectorAndButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const AmountSelector = ({ onContinue }) => {
  const {
    targetTokenAddress,
    network,
    usdAmount,
    setValue,
    selectedPaymentToken,
    setSelectedPaymentToken,
    setPayingWith,
    setFromUsdcDirection,
    fromUsdcDirection,
    targetTokenAmount,
  } = useGiftForm();

  const targetToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, targetTokenAddress),
  );

  const tokenSymbolRefElement = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  const [fullyRendered, setFullyRendered] = useState(false);
  const inputRef = useRef(null);
  const [loadingTokenAmount, setLoadingTokenAmount] = useState(false);

  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, network, 'USDC'),
  );

  const userSelectedPaymentToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(
      state,
      network,
      selectedPaymentToken?.address,
    ),
  );

  const userSelectedTokenBalance = useSelector((state) =>
    selectTokenBalance(network, selectedPaymentToken?.address)(state),
  );

  const [
    userSelectedPaymentTokenUsdcBalance,
    setUserSelectedPaymentTokenUsdcBalance,
  ] = useState(0);

  const dispatch = useDispatch();

  const tokensWithUSDBalance = useSelector(selectTokensWithUSDBalance);
  const [tokensWithEnoughBalance, setTokensWithEnoughBalance] = useState([]);
  const [tokensWithoutEnoughBalance, setTokensWitoutEnoughBalance] = useState(
    [],
  );
  const [shouldDeposit, setShouldDeposit] = useState(true);

  const [swapQuote, setSwapQuote] = useState({
    fromToken: null,
    toToken: null,
    amount: null,
  });

  const [triggerCreateOrder] = useLazyCreateOrderQuery();

  useEffect(() => {
    if (tokenSymbolRefElement?.current) {
      setLabelWidth(tokenSymbolRefElement?.current?.offsetWidth || 0);
    }
  }, [fromUsdcDirection, targetToken?.symbol, targetTokenAmount, usdAmount]);

  useEffect(() => {
    if (userSelectedPaymentToken) {
      const usToken = tokensWithEnoughBalance.find(
        (token) => token.address === userSelectedPaymentToken?.address,
      );
      if (usToken) {
        setUserSelectedPaymentTokenUsdcBalance(usToken.usdValue);
      } else {
        setUserSelectedPaymentTokenUsdcBalance(0);
      }
    }
  }, [userSelectedPaymentToken, tokensWithEnoughBalance]);

  useEffect(() => {
    if (targetToken && usdcToken && usdAmount) {
      if (tokensWithEnoughBalance.length > 0) {
        setShouldDeposit(false);
        const isSelectedPaymentTokenInTokensWithEnoughBalance =
          tokensWithEnoughBalance.some(
            (token) => token.address === userSelectedPaymentToken?.address,
          );
        if (isSelectedPaymentTokenInTokensWithEnoughBalance) {
          setSelectedPaymentToken(userSelectedPaymentToken);
        } else {
          const isTargetTokenInTokensWithEnoughBalance =
            tokensWithEnoughBalance.some(
              (token) => token.address === targetToken.address,
            );
          if (isTargetTokenInTokensWithEnoughBalance) {
            setSelectedPaymentToken(targetToken);
          } else {
            const isUsdcTokenInTokensWithEnoughBalance =
              tokensWithEnoughBalance.some(
                (token) => token.address === usdcToken.address,
              );
            if (isUsdcTokenInTokensWithEnoughBalance) {
              setSelectedPaymentToken(usdcToken);
            } else {
              setSelectedPaymentToken(tokensWithEnoughBalance[0]);
            }
          }
        }
      } else {
        setShouldDeposit(true);
        // No tokens with enough balance, prompt user to add funds
      }
    }
  }, [
    tokensWithEnoughBalance,
    userSelectedPaymentToken,
    targetToken,
    usdcToken,
    usdAmount,
  ]);

  useEffect(() => {
    if (tokensWithUSDBalance && usdAmount) {
      setTokensWithEnoughBalance(
        tokensWithUSDBalance.filter((token) => token.usdValue >= usdAmount),
      );
      setTokensWitoutEnoughBalance(
        tokensWithUSDBalance.filter((token) => token.usdValue < usdAmount),
      );
    }
  }, [tokensWithUSDBalance, usdAmount]);

  useEffect(() => {
    if (usdcToken && targetToken && !targetTokenAmount && fromUsdcDirection) {
      setSwapQuote({
        fromToken: usdcToken,
        toToken: targetToken,
        amount: usdAmount,
      });
    }
  }, [usdcToken, targetTokenAmount, targetToken, usdAmount]);

  useEffect(() => {
    if (
      swapQuote.fromToken &&
      swapQuote.toToken &&
      swapQuote.amount &&
      parseFloat(swapQuote.amount) > 0
    ) {
      const fetchQuote = async () => {
        setSwapQuote({
          fromToken: null,
          toToken: null,
          amount: null,
        });
        setLoadingTokenAmount(true);
        if (
          swapQuote.fromToken.address === usdcToken.address &&
          swapQuote.toToken.address === usdcToken.address
        ) {
          setValue('usdAmount', swapQuote.amount);
          setValue('targetTokenAmount', swapQuote.amount);
        } else {
          const orderResult = await triggerCreateOrder({
            inputMint: swapQuote.fromToken.address,
            outputMint: swapQuote.toToken.address,
            amount: toTokenAmount(
              swapQuote.amount,
              swapQuote.fromToken.decimals,
            ),
          }).unwrap();
          if (orderResult?.outAmount) {
            const exactAmountNeeded = orderResult.outAmount;
            const formatedTokenAmount = fromTokenAmount(
              exactAmountNeeded,
              swapQuote.toToken?.decimals,
            );
            if (swapQuote.fromToken.address === usdcToken.address) {
              setValue('targetTokenAmount', formatedTokenAmount);
            } else {
              setValue('usdAmount', formatedTokenAmount);
            }
          }
        }
        setLoadingTokenAmount(false);
      };
      fetchQuote();
    }
  }, [swapQuote]);

  useEffect(() => {
    if (!fullyRendered) return;
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, [fullyRendered, inputRef.current]);

  return (
    <>
      <AmountsContainer>
        <InputContainer>
          {fromUsdcDirection && (
            <InsideInputContainer>
              <span>$</span>
            </InsideInputContainer>
          )}
          <StyledInput
            key={tokenSymbolRefElement.current?.offsetWidth}
            style={{
              maxWidth: `${labelWidth + 42}px`,
            }}
            autoFocus={true}
            ref={inputRef}
            value={fromUsdcDirection ? usdAmount : targetTokenAmount}
            id='amount-input'
            inputMode='numeric'
            onChange={(e) => {
              if (fromUsdcDirection) {
                setSwapQuote({
                  fromToken: usdcToken,
                  toToken: targetToken,
                  amount: e.target.value,
                });
                setValue('usdAmount', e.target.value);
              } else {
                setValue('targetTokenAmount', e.target.value);
                setSwapQuote({
                  fromToken: targetToken,
                  toToken: usdcToken,
                  amount: e.target.value,
                });
              }
            }}
          />
          {!fromUsdcDirection && (
            <InsideInputContainer>
              <span>{targetToken?.symbol}</span>
            </InsideInputContainer>
          )}
        </InputContainer>
        <InsideInputContainer style={{ position: 'absolute' }}>
          <span style={{ color: 'transparent' }} ref={tokenSymbolRefElement}>
            {fromUsdcDirection ? usdAmount : targetTokenAmount}
          </span>
        </InsideInputContainer>
        {targetToken?.symbol !== 'USDC' && (
          <SwapAmountContainer
            onClick={() => setFromUsdcDirection(!fromUsdcDirection)}
          >
            <BodyLarge>
              {fromUsdcDirection
                ? `${targetTokenAmount} ${targetToken?.symbol}`
                : `$${usdAmount}`}
            </BodyLarge>
            <WalletSwapIcon width={18} height={18} />
          </SwapAmountContainer>
        )}
      </AmountsContainer>
      <AmountButtonContainer>
        <AmountButton
          onClick={() => {
            setValue('usdAmount', 30);
            hapticsImpactLight();
            setSwapQuote({
              fromToken: usdcToken,
              toToken: targetToken,
              amount: 30,
            });
          }}
        >
          $30
        </AmountButton>
        <AmountButton
          onClick={() => {
            setValue('usdAmount', 40);
            hapticsImpactLight();
            setSwapQuote({
              fromToken: usdcToken,
              toToken: targetToken,
              amount: 40,
            });
          }}
        >
          $40
        </AmountButton>
        <AmountButton
          onClick={() => {
            setValue('usdAmount', 50);
            hapticsImpactLight();
            setSwapQuote({
              fromToken: usdcToken,
              toToken: targetToken,
              amount: 50,
            });
          }}
        >
          $50
        </AmountButton>
        <AmountButton
          onClick={() => {
            setValue('usdAmount', 100);
            hapticsImpactLight();
            setSwapQuote({
              fromToken: usdcToken,
              toToken: targetToken,
              amount: 100,
            });
          }}
        >
          $100
        </AmountButton>
      </AmountButtonContainer>
      <PaymentSelectorAndButtonContainer>
        <PaymentSelectorContainer
          style={shouldDeposit ? { cursor: 'default' } : { cursor: 'pointer' }}
          onClick={() => {
            if (!shouldDeposit) {
              dispatch(
                openModal({
                  name: 'paymentSuggestion',
                  props: {
                    amount: usdAmount,
                    network: network,
                    targetToken: targetToken,
                    tokenOptions: tokensWithEnoughBalance,
                    disabledTokenOptions: tokensWithoutEnoughBalance,
                    onCancel: () => {
                      dispatch(
                        openModal({
                          name: 'giftFlow',
                          props: {
                            initialStep: 1,
                          },
                        }),
                      );
                    },
                    onPaymentMethodSelected: (paymentData) => {
                      setSelectedPaymentToken(paymentData.token);
                      dispatch(
                        openModal({
                          name: 'giftFlow',
                          props: {
                            initialStep: 1,
                          },
                        }),
                      );
                    },
                  },
                }),
              );
            }
          }}
        >
          {shouldDeposit ? (
            <>
              <TokenImage src={usdcToken?.logoURI} />
              <PaymentTokenInfoText>{usdcToken?.symbol}</PaymentTokenInfoText>
            </>
          ) : (
            <>
              <TokenImage src={selectedPaymentToken?.logoURI} />
              <PaymentTokenInfoText>
                {selectedPaymentToken?.symbol} (
                {fromUsdcDirection
                  ? formatUSDEnsure2Digits(userSelectedPaymentTokenUsdcBalance)
                  : userSelectedTokenBalance}
                )
              </PaymentTokenInfoText>
              <ChevronRightIcon width={12} height={12} />
            </>
          )}
        </PaymentSelectorContainer>
        <Button
          fullWidth
          loading={loadingTokenAmount}
          onClick={async () => {
            if (shouldDeposit) {
              dispatch(openModal({ name: 'addFunds' }));
            } else {
              setPayingWith(selectedPaymentToken);
              onContinue();
            }
          }}
        >
          {shouldDeposit ? 'Deposit' : 'Continue'}
        </Button>
      </PaymentSelectorAndButtonContainer>
    </>
  );
};

export default AmountSelector;
